import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import listPlugin from '@fullcalendar/list';
import InfiniteScrollCalendar from './InfiniteScrollCalendar';
import InfiniteScrollWeekCalendar from './InfiniteScrollWeekCalendar';
import PresenceGridDemo from './PresenceGridDemo';
import ErrorBoundary from './ErrorBoundary';
import NotificationBanner from './NotificationBanner';

const API_URL = '/wythe/v1/event';
const FEED_URL = '/wythe/v1/feed';

function App() {
  const [view, setView] = useState('calendar'); // 'calendar' or 'feed'
  const [currentCalendarView, setCurrentCalendarView] = useState(localStorage.getItem('wythe_view') || 'infiniteScroll');

  // Only use saved date if it's from today (to prevent "Today" button taking you to a months-old saved date)

  const getInitialDate = () => {
    const saved = localStorage.getItem('wythe_date');
    if (!saved) return new Date().toISOString();
    
    const savedDate = new Date(saved);
    const now = new Date();
    // If saved date is more than 24 hours old, just use now
    if (Math.abs(now - savedDate) > 24 * 60 * 60 * 1000) {
        return now.toISOString();
    }
    return saved;
  };
  // We use a constant for initialDate to prevent FullCalendar from re-mounting/resetting when we navigate
  const [initialDate] = useState(getInitialDate());
  const [currentUser, setCurrentUser] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [feedEvents, setFeedEvents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [needsSignup, setNeedsSignup] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  
  const addNotification = useCallback((message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);

  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);
  const calendarRef = useRef(null);

  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const [viewAs, setViewAs] = useState(null);
  const [friends, setFriends] = useState([]);
  const [scrollToTodayTrigger, setScrollToTodayTrigger] = useState(0);
  const [currentTitle, setCurrentTitle] = useState('');
  const [publicPreview, setPublicPreview] = useState(false);

  const slotMinTime = useMemo(() => {
    const hour = currentUser?.defaultStartHour ?? 6;
    return `${hour.toString().padStart(2, '0')}:00:00`;
  }, [currentUser]);

  const pathParts = window.location.pathname.split('/');
  let profileSlug = pathParts[1] === 'u' ? pathParts[2] : null;
  if (!profileSlug && pathParts[1].startsWith('@')) {
      profileSlug = pathParts[1].substring(1);
  }
  const isOwnProfile = !profileSlug;

  const formatEvent = useCallback((event) => {
    const isAllDay = event.isAllDay || event.IsAllDay;
    let start = event.startTime || event.StartTime;
    let end = event.endTime || event.EndTime;

    if (isAllDay) {
        const d = new Date(start * 1000);
        start = d.toISOString().split('T')[0];
        
        if (end) {
            const ed = new Date(end * 1000);
            end = ed.toISOString().split('T')[0];
        }
    } else {
        start = new Date(start * 1000);
        if (end) end = new Date(end * 1000);
    }

    return {
        id: event.id || event.ID,
        title: event.displayTitle || event.title || event.Title,
        start: start,
        end: end,
        allDay: isAllDay,
        color: event.calendarColor || event.CalendarColor || '#0071e3',
        extendedProps: {
            description: event.displayDescription || event.description || event.Description,
            visibility: event.defaultVisibility || event.DefaultVisibility,
            calendarName: event.calendarName || event.CalendarName,
            visibilityOverrides: event.visibilityOverrides || event.VisibilityOverrides || []
        }
    };
  }, []);

  const formatAndSetEvents = useCallback((data) => {
    const formattedEvents = (data || []).map(formatEvent);
    setEvents(formattedEvents);
  }, [formatEvent]);

  const fetchMe = useCallback(async () => {
    try {
        const response = await fetch('/wythe/v1/auth/me');
        if (response.ok) {
            const user = await response.json();
            setCurrentUser(user);
            if (!user.phone) {
                setNeedsSignup(true);
            }
        } else if (response.status === 404) {
            setNeedsSignup(true);
        } else if (response.status === 401) {
            setNeedsLogin(true);
        }
    } catch (error) {
        console.error('Failed to fetch user profile', error);
    }
  }, []);

  const fetchFriends = useCallback(async () => {
    try {
      const resp = await fetch('/wythe/v1/friends');
      if (resp.ok) {
        const data = await resp.json();
        setFriends(data.filter(f => f.status === 'accepted'));
      }
    } catch (err) {
      console.error('Failed to fetch friends', err);
    }
  }, []);

  const fetchFeed = useCallback(async () => {
    try {
        const response = await fetch(FEED_URL);
        if (response.ok) {
            const data = await response.json();
            setFeedEvents(data || []);
        } else if (response.status === 404) {
            setFeedEvents([]);
        }
    } catch (error) {
        addNotification('Failed to fetch feed', 'danger');
    }
  }, [addNotification]);

  const fetchUserEvents = useCallback(async (slug) => {
    try {
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);
      const queryParam = isUUID ? `id=${slug}` : `username=${slug}`;
      
      const api = calendarRef.current?.getApi();
      let rangeParams = "";
      if (api) {
          const view = api.view;
          // Pre-fetch: request 1 year before and 3 months after the current view range
          const start = Math.floor(view.activeStart.getTime() / 1000) - (365 * 24 * 60 * 60);
          const end = Math.floor(view.activeEnd.getTime() / 1000) + (90 * 24 * 60 * 60);
          rangeParams = `&start=${start}&end=${end}`;
      } else {
          // If in custom infinite view, fetch a wide range around now
          const now = Math.floor(Date.now() / 1000);
          const start = now - (365 * 24 * 60 * 60);
          const end = now + (365 * 24 * 60 * 60);
          rangeParams = `&start=${start}&end=${end}`;
      }

      const previewParam = publicPreview ? '&preview=public' : '';
      const response = await fetch(`/wythe/v1/user/events?${queryParam}${rangeParams}${previewParam}`);

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      const data = await response.json();
      formatAndSetEvents(data);
    } catch (error) {
      addNotification(error.message, 'danger');
    }
  }, [formatAndSetEvents, publicPreview, addNotification]);

  const fetchProfile = useCallback(async (slug) => {
    try {
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);
        const queryParam = isUUID ? `id=${slug}` : `username=${slug}`;
        
        const response = await fetch(`/wythe/v1/user/profile?${queryParam}`);
        if (response.ok) {
            const data = await response.json();
            setProfileUser(data);
            fetchUserEvents(slug);
        } else {
            addNotification('User not found', 'danger');
        }
    } catch (error) {
        console.error('Failed to fetch user profile', error);
    }
  }, [fetchUserEvents, addNotification]);

  const fetchEvents = useCallback(async () => {
    try {
      const api = calendarRef.current?.getApi();
      let rangeParams = "";
      if (api) {
          const view = api.view;
          // Pre-fetch: request 1 year before and 3 months after the current view range
          const start = Math.floor(view.activeStart.getTime() / 1000) - (365 * 24 * 60 * 60);
          const end = Math.floor(view.activeEnd.getTime() / 1000) + (90 * 24 * 60 * 60);
          rangeParams = `?start=${start}&end=${end}`;
      } else {
          // Wide range for infinite scroll
          const now = Math.floor(Date.now() / 1000);
          const start = now - (365 * 24 * 60 * 60);
          const end = now + (365 * 24 * 60 * 60);
          rangeParams = `?start=${start}&end=${end}`;
      }

      let url = API_URL + rangeParams;
      if (viewAs) {
        url = `/wythe/v1/event/viewas${rangeParams}${rangeParams ? '&' : '?'}viewerId=${viewAs}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        if (response.status === 404) {
            setEvents([]);
            return;
        }
        throw new Error('Failed to fetch events');
      }
      const data = await response.json();
      formatAndSetEvents(data);
    } catch (error) {
      addNotification(error.message, 'danger');
    }
  }, [viewAs, formatAndSetEvents, addNotification]);

  useEffect(() => {
    const init = async () => {
        await fetchMe();
    };
    init();
  }, [fetchMe]);

  useEffect(() => {
    if (!currentUser || needsSignup) return;

    if (profileSlug) {
        fetchProfile(profileSlug);
    } else {
        fetchEvents();
        fetchFriends();
    }
    fetchFeed();

    // Ensure we are scrolled to today on initial load of custom views
    setTimeout(() => {
        const api = calendarRef.current?.getApi();
        if (api && (api.view.type === 'infiniteScroll' || api.view.type === 'infiniteScrollWeek')) {
            api.scrollToTime('00:00:00'); 
        }
    }, 500);
  }, [profileSlug, currentUser, needsSignup, fetchProfile, fetchEvents, fetchFriends, fetchFeed, currentCalendarView, publicPreview]);

  const handleEventClick = useCallback(async (clickInfo) => {
    const eventID = clickInfo.event.id;
    try {
        const resp = await fetch(`${API_URL}?id=${eventID}`);
        if (resp.ok) {
            const data = await resp.json();
            const fullEvent = Array.isArray(data) ? data[0] : data;
            if (fullEvent) {
                setSelectedEvent(formatEvent(fullEvent));
                setShowEventModal(true);
            }
        }
    } catch (err) {
        console.error("Failed to fetch full event details", err);
        setSelectedEvent({
            id: clickInfo.event.id,
            title: clickInfo.event.title,
            start: clickInfo.event.start,
            end: clickInfo.event.end,
            allDay: clickInfo.event.allDay,
            extendedProps: clickInfo.event.extendedProps || {}
        });
        setShowEventModal(true);
    }
  }, [formatEvent]);

  const handleDateClick = useCallback((arg) => {
    setSelectedDate(arg.date);
    setEditingEvent(null);
    setShowFormModal(true);
  }, []);

  const handleCloseModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
    setShowFormModal(false);
    setEditingEvent(null);
    setSelectedDate(null);
  };

  const handleSaveEvent = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const eventData = {
        title: formData.get('title'),
        description: formData.get('description'),
        startTime: Math.floor(new Date(formData.get('startTime')).getTime() / 1000),
        endTime: Math.floor(new Date(formData.get('endTime')).getTime() / 1000),
        attendeeIds: formData.getAll('attendeeIds'),
    };

    if (eventData.endTime < eventData.startTime) {
        addNotification("End time must be at or after start time", 'warning');
        return;
    }
    const isEditing = !!editingEvent;
    const url = isEditing ? `${API_URL}?id=${editingEvent.id}` : API_URL;
    const method = isEditing ? 'PUT' : 'POST';

    try {
        const response = await fetch(url, {
            method: method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(eventData),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to ${isEditing ? 'update' : 'create'} event`);
        }

        if (profileSlug) {
            fetchUserEvents(profileSlug);
        } else {
            fetchEvents();
        }
        handleCloseModal();
    } catch (error) {
        addNotification(error.message, 'danger');
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent || !window.confirm("Are you sure you want to delete this event?")) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}?id=${selectedEvent.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to delete event');
      }
      fetchEvents();
      handleCloseModal();
    } catch (error) {
      addNotification(error.message, 'danger');
    }
  };

  const handleLogout = () => window.location.href = '/auth/google/logout';

  const refreshEventModal = () => {
      if (selectedEvent) {
          handleEventClick({ event: { id: selectedEvent.id } });
      }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const signupData = {
        displayName: formData.get('displayName'),
        phone: formData.get('phone'),
    };

    try {
        const response = await fetch('/wythe/v1/auth/signup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(signupData),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to create account');
        }

        const newUser = await response.json();
        setCurrentUser(newUser);
        setNeedsSignup(false);
        fetchEvents();
        fetchFriends();
    } catch (error) {
        addNotification(error.message, 'danger');
    }
  };

  if (needsLogin) {
      return (
        <div className="d-flex align-items-center justify-content-center vh-100" style={{ backgroundColor: '#1a1a1b', color: '#ffffff' }}>
          <div className="w-100" style={{ maxWidth: '400px', padding: '24px' }}>
            <h1 className="h3 fw-bold mb-3 text-center">Log In</h1>
            <p className="small text-center mb-4" style={{ color: '#818384' }}>
              By continuing, you agree to our <a href="#!" onClick={(e) => e.preventDefault()} style={{ color: '#4fbcff', textDecoration: 'none' }}>User Agreement</a> and acknowledge that you understand the <a href="#!" onClick={(e) => e.preventDefault()} style={{ color: '#4fbcff', textDecoration: 'none' }}>Privacy Policy</a>.
            </p>
            
            <div className="d-grid gap-2">
              <button 
                className="btn btn-light d-flex align-items-center justify-content-center py-2 fw-bold"
                style={{ borderRadius: '999px', fontSize: '14px' }}
                onClick={() => window.location.href = '/auth/google/login'}
              >
                <img src="https://www.gstatic.com/images/branding/product/1x/googleg_48dp.png" alt="Google" style={{ width: '20px', height: '20px', marginRight: '10px' }} />
                Continue with Google
              </button>
              
              <button 
                className="btn btn-outline-light d-flex align-items-center justify-content-center py-2 fw-bold disabled"
                style={{ borderRadius: '999px', fontSize: '14px', borderColor: '#ffffff', opacity: 0.6 }}
              >
                <i className="bi bi-apple me-2"></i>
                Continue with Apple
              </button>
            </div>

            <div className="d-flex align-items-center my-4">
              <hr className="flex-grow-1" style={{ borderColor: '#343536' }} />
              <span className="px-3 small fw-bold" style={{ color: '#818384' }}>OR</span>
              <hr className="flex-grow-1" style={{ borderColor: '#343536' }} />
            </div>

            <div className="mb-3">
              <input 
                type="text" 
                className="form-control border-0 py-3 px-3" 
                placeholder="Email or username *"
                style={{ backgroundColor: '#272729', color: '#ffffff', borderRadius: '12px' }}
                disabled
              />
            </div>
            <div className="mb-4">
              <input 
                type="password" 
                className="form-control border-0 py-3 px-3" 
                placeholder="Password *"
                style={{ backgroundColor: '#272729', color: '#ffffff', borderRadius: '12px' }}
                disabled
              />
            </div>
            
            <p className="small text-center" style={{ color: '#818384' }}>
              New to Wythe? <a href="/" onClick={() => window.location.href = '/auth/google/login'} style={{ color: '#4fbcff', textDecoration: 'none' }}>Sign Up</a>
            </p>
          </div>
        </div>
      );
  }

  if (needsSignup) {
      const signupError = notifications.find(n => n.type === 'danger')?.message;
      return <SignupForm onSignup={handleSignup} error={signupError} currentUser={currentUser} />;
  }

  const handleTodayClick = () => {
    if (currentCalendarView === 'infiniteScroll' || currentCalendarView === 'infiniteScrollWeek') {
        setScrollToTodayTrigger(prev => prev + 1);
    } else {
        const api = calendarRef.current?.getApi();
        if (api) {
            api.today();
            setTimeout(() => {
                const todayEl = document.querySelector('.fc-day-today');
                if (todayEl) {
                    todayEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        }
    }
  };

  const deployHash = document.body.dataset.hash || '';
  const deployTime = document.body.dataset.deployTime || '';

  return (
    <div className="container-fluid px-4 mt-4">
      <NotificationBanner notifications={notifications} onDismiss={dismissNotification} />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
            <h1>{isOwnProfile ? 'My Wythe' : `${profileUser?.displayName || profileSlug}'s Wythe`}</h1>
            {currentUser && (
                <div className="text-muted small">
                    Logged in as: {currentUser.username ? `@${currentUser.username}` : currentUser.email}
                    {isOwnProfile && currentUser.username && (
                        <span className="ms-2">| Public Link: <a href={`/u/${currentUser.username}`} target="_blank" rel="noopener noreferrer">{window.location.origin}/u/{currentUser.username}</a></span>
                    )}
                    {deployHash && <span className="ms-2">| Hash: {deployHash.substring(0, 7)}</span>}
                    {deployTime && <span className="ms-2">| Deployed: {new Date(deployTime).toLocaleString()}</span>}
                    {!isOwnProfile && <span className="ms-2">| <a href="/">Back to my calendar</a></span>}
                </div>
            )}
        </div>
        <div className="d-flex align-items-center">
          {!isOwnProfile && (
              <div className="form-check form-switch me-3">
                  <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="publicPreview" 
                      checked={publicPreview} 
                      onChange={(e) => setPublicPreview(e.target.checked)} 
                  />
                  <label className="form-check-label small text-muted" htmlFor="publicPreview">Public Preview</label>
              </div>
          )}
          {isOwnProfile && view === 'calendar' && (
              <div className="me-2 d-flex align-items-center">
                  <label className="me-2 small text-muted text-nowrap">View as:</label>
                  <select 
                      className="form-select form-select-sm" 
                      style={{ width: 'auto', minWidth: '120px' }}
                      value={viewAs || ''} 
                      onChange={(e) => setViewAs(e.target.value || null)}
                  >
                      <option value="">Myself (Full)</option>
                      <option value="public">Public Viewer</option>
                      {(friends || []).map(f => (
                          <option key={f.id} value={f.id}>{f.displayName}</option>
                      ))}
                  </select>
              </div>
          )}
          {isOwnProfile && (
              <button 
                  className={`btn ${view === 'feed' ? 'btn-primary' : 'btn-outline-primary'} me-2`} 
                  onClick={() => setView(view === 'calendar' ? 'feed' : 'calendar')}
              >
                  {view === 'calendar' ? 'Friends Feed' : 'Back to Calendar'}
              </button>
          )}
          <a href="/admin" className="btn btn-outline-secondary me-2">Dash</a>
          <button className="btn btn-danger" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {view === 'calendar' ? (
        <ErrorBoundary>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center">
                <button className="btn btn-outline-secondary btn-sm me-2" onClick={handleTodayClick}>Today</button>
                {(currentCalendarView !== 'infiniteScroll' && currentCalendarView !== 'infiniteScrollWeek') && (
                    <div className="btn-group btn-group-sm me-3">
                        <button className="btn btn-outline-secondary" onClick={() => calendarRef.current?.getApi().prev()}>&lt;</button>
                        <button className="btn btn-outline-secondary" onClick={() => calendarRef.current?.getApi().next()}>&gt;</button>
                    </div>
                )}
                <h4 className="mb-0 ms-2 fw-bold">{currentTitle}</h4>
            </div>
            <div className="btn-group shadow-sm">
                <button 
                    className={`btn btn-sm ${currentCalendarView === 'infiniteScroll' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => {
                        setCurrentCalendarView('infiniteScroll');
                        localStorage.setItem('wythe_view', 'infiniteScroll');
                    }}
                >
                    Month
                </button>
                <button 
                    className={`btn btn-sm ${currentCalendarView === 'infiniteScrollWeek' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => {
                        setCurrentCalendarView('infiniteScrollWeek');
                        localStorage.setItem('wythe_view', 'infiniteScrollWeek');
                    }}
                >
                    Week
                </button>
                <button 
                    className={`btn btn-sm ${currentCalendarView === 'presence' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => {
                        setCurrentCalendarView('presence');
                        localStorage.setItem('wythe_view', 'presence');
                    }}
                >
                    Presence
                </button>
                <button 
                    className={`btn btn-sm ${currentCalendarView === 'listMonth' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => {
                        setCurrentCalendarView('listMonth');
                        localStorage.setItem('wythe_view', 'listMonth');
                    }}
                >
                    Agenda
                </button>
            </div>
          </div>

          {currentCalendarView === 'infiniteScroll' ? (
            <InfiniteScrollCalendar 
                events={events}
                onEventClick={(info) => handleEventClick({ event: info.event })}
                onDateClick={isOwnProfile ? handleDateClick : null}
                initialDate={initialDate}
                scrollToTodayTrigger={scrollToTodayTrigger}
                onTitleChange={setCurrentTitle}
            />
          ) : currentCalendarView === 'infiniteScrollWeek' ? (
            <InfiniteScrollWeekCalendar 
                events={events}
                onEventClick={(info) => handleEventClick({ event: info.event })}
                onDateClick={isOwnProfile ? handleDateClick : null}
                initialDate={initialDate}
                scrollToTodayTrigger={scrollToTodayTrigger}
                onTitleChange={setCurrentTitle}
            />
          ) : currentCalendarView === 'presence' ? (
            <PresenceGridDemo />
          ) : (
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin, listPlugin]}
              initialView={currentCalendarView}
              initialDate={initialDate}
              headerToolbar={false}
              editable={isOwnProfile}
              selectable={isOwnProfile}
              slotMinTime={slotMinTime}
              scrollTime={slotMinTime}
              events={events}
              eventClick={handleEventClick}
              dateClick={isOwnProfile ? handleDateClick : null}
              datesSet={(arg) => {
                  setCurrentCalendarView(arg.view.type);
                  setCurrentTitle(arg.view.title);
                  localStorage.setItem('wythe_view', arg.view.type);
                  localStorage.setItem('wythe_date', arg.view.currentStart.toISOString());
              }}
              height="85vh"
              stickyHeaderDates={true}
              allDaySlot={true}
              nowIndicator={true}
            />
          )}
        </ErrorBoundary>
      ) : (
        <div className="friends-feed">
          <h3>Recent activity from your friends</h3>
          <ul className="list-group">
            {(feedEvents || []).map(event => (
              <li key={event.id} className="list-group-item">
                <div className="d-flex w-100 justify-content-between">
                  <h5 className="mb-1">{event.title}</h5>
                  <small>{new Date(event.startTime * 1000).toLocaleDateString()}</small>
                </div>
                <p className="mb-1">{event.description || 'No description'}</p>
                <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                        {event.isAllDay || event.IsAllDay ? 'All Day' : `${new Date(event.startTime * 1000).toLocaleTimeString()} - ${new Date(event.endTime * 1000).toLocaleTimeString()}`}
                    </small>
                    <small>
                        By: <a href={`/u/${event.ownerUsername || event.ownerId}`}>{event.ownerName}</a>
                    </small>
                </div>
              </li>
            ))}

            {feedEvents.length === 0 && <li className="list-group-item">No friend activity found.</li>}
          </ul>
        </div>
      )}

      {showEventModal && selectedEvent && (
        <div className="modal" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedEvent.title}</h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-2">Calendar: {selectedEvent.extendedProps.calendarName || 'Main'}</p>
                {selectedEvent.allDay || selectedEvent.extendedProps.visibility === 'time_block' ? (
                    <div>
                        {selectedEvent.allDay && <p className="badge bg-primary">All Day Event</p>}
                        {selectedEvent.extendedProps.visibility === 'time_block' ? (
                            <p className="text-muted">Busy block - details hidden</p>
                        ) : (
                            <>
                                <p><strong>Description:</strong> {selectedEvent.extendedProps.description || 'No description'}</p>
                                <p><strong>Date:</strong> {new Date(selectedEvent.start).toLocaleDateString()}</p>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        <p><strong>Description:</strong> {selectedEvent.extendedProps.description || 'No description'}</p>
                        <p><strong>Start:</strong> {new Date(selectedEvent.start).toLocaleString()}</p>
                        <p><strong>End:</strong> {new Date(selectedEvent.end).toLocaleString()}</p>
                    </>
                )}

                {isOwnProfile && (
                    <div className="mt-4 border-top pt-3">
                        <h6>Visibility Overrides</h6>
                        <VisibilityManager event={selectedEvent} onUpdate={refreshEventModal} />
                    </div>
                )}
              </div>
              <div className="modal-footer">
                {isOwnProfile && <button type="button" className="btn btn-outline-danger me-auto" onClick={handleDeleteEvent}>Delete</button>}
                {isOwnProfile && <button type="button" className="btn btn-primary" onClick={() => { setShowEventModal(false); setEditingEvent(selectedEvent); setShowFormModal(true); }}>Edit</button>}
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
{showFormModal && (
  <EventForm 
      event={editingEvent} 
      date={selectedDate} 
      friends={friends}
      onSave={handleSaveEvent} 
      onClose={handleCloseModal} 
  />
)}

      {(showEventModal || showFormModal) && <div className="modal-backdrop fade show"></div>}
    </div>
  );
}

function VisibilityManager({ event, onUpdate }) {
    const [friends, setFriends] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const [fResp, gResp] = await Promise.all([
                fetch('/wythe/v1/friends'),
                fetch('/wythe/v1/friends/groups')
            ]);
            if (fResp.ok) setFriends(await fResp.json());
            if (gResp.ok) setGroups(await gResp.json());
        };
        fetchData();
    }, []);

    const handleAddOverride = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const target = formData.get('target').split(':'); // type:id
        const override = {
            targetType: target[0],
            targetId: target[1],
            visibility: formData.get('visibility')
        };

        setLoading(true);
        try {
            const resp = await fetch(`/wythe/v1/event/overrides?eventId=${event.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(override)
            });
            if (resp.ok) onUpdate();
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveOverride = async (targetType, targetId) => {
        setLoading(true);
        try {
            const resp = await fetch(`/wythe/v1/event/overrides?eventId=${event.id}&targetType=${targetType}&targetId=${targetId}`, {
                method: 'DELETE'
            });
            if (resp.ok) onUpdate();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="small">
            <ul className="list-group list-group-flush mb-3">
                {(event.extendedProps.visibilityOverrides || []).map(o => (
                    <li key={`${o.targetType}-${o.targetId}`} className="list-group-item d-flex justify-content-between align-items-center px-0">
                        <span>
                            <span className="badge bg-light text-dark border me-2">
                                {o.targetType === 'user' ? '👤' : (o.targetType === 'everyone' ? '🌍' : '👥')}
                            </span>
                            {o.targetType === 'everyone' ? 'All Friends' : o.targetName} → <strong>{o.visibility}</strong>
                        </span>
                        <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleRemoveOverride(o.targetType, o.targetId)}>Remove</button>
                    </li>
                ))}
                {(!event.extendedProps.visibilityOverrides || event.extendedProps.visibilityOverrides.length === 0) && (
                    <li className="list-group-item text-muted px-0">No overrides set.</li>
                )}
            </ul>

            <form onSubmit={handleAddOverride} className="row g-2 align-items-end">
                <div className="col-5">
                    <label className="form-label mb-1">Target</label>
                    <select name="target" className="form-select form-select-sm" required>
                        <option value="">Select...</option>
                        <option value="everyone:everyone">All Friends</option>
                        <optgroup label="Friends">
                            {(friends || []).filter(f => f.status === 'accepted').map(f => (
                                <option key={f.id} value={`user:${f.id}`}>{f.displayName}</option>
                            ))}
                        </optgroup>
                        <optgroup label="Groups">
                            {(groups || []).map(g => (
                                <option key={g.id} value={`group:${g.id}`}>{g.name}</option>
                            ))}
                        </optgroup>
                    </select>
                </div>
                <div className="col-4">
                    <label className="form-label mb-1">Visibility</label>
                    <select name="visibility" className="form-select form-select-sm" required>
                        <option value="full">Full Details</option>
                        <option value="time_block">Time Block Only</option>
                        <option value="public">Public (Everyone)</option>
                        <option value="hidden">Hidden</option>
                    </select>
                </div>
                <div className="col-3">
                    <button type="submit" className="btn btn-sm btn-primary w-100" disabled={loading}>Add</button>
                </div>
            </form>
        </div>
    );
}

function EventForm({ event, date, friends, onSave, onClose }) {
  const isEditing = !!event;
  const currentAttendees = event?.extendedProps?.attendeeIds || [];

  const toInputString = (d) => {
    if (!d) return '';
    return new Date(d.valueOf() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  }

  const getStartTime = () => {
    if (event?.start) return toInputString(new Date(event.start));
    if (date) return toInputString(date);
    return '';
  }

  const getEndTime = () => {
    if (event?.end) return toInputString(new Date(event.end));
    if (date) {
        // Default new events to 1 hour duration
        return toInputString(new Date(date.getTime() + 60 * 60 * 1000));
    }
    return '';
  }

  return (
    <div className="modal" style={{ display: 'block' }} tabIndex="-1">
      <div className="modal-dialog">
        <div className="modal-content">
          <form onSubmit={onSave}>
            <div className="modal-header">
              <h5 className="modal-title">{isEditing ? 'Edit Event' : 'New Event'}</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label htmlFor="title" className="form-label">Title</label>
                <input type="text" className="form-control" id="title" name="title" defaultValue={event?.title || ''} required />
              </div>
              <div className="mb-3">
                <label htmlFor="description" className="form-label">Description</label>
                <textarea className="form-control" id="description" name="description" defaultValue={event?.extendedProps?.description || ''}></textarea>
              </div>
              <div className="mb-3">
                <label htmlFor="startTime" className="form-label">Start Time</label>
                <input type="datetime-local" className="form-control" id="startTime" name="startTime" defaultValue={getStartTime()} required />
              </div>
              <div className="mb-3">
                <label htmlFor="endTime" className="form-label">End Time</label>
                <input type="datetime-local" className="form-control" id="endTime" name="endTime" defaultValue={getEndTime()} required />
              </div>
              {friends && (friends || []).length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">Invite Friends</label>
                    <div className="border p-2 rounded" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {(friends || []).map(friend => (
                            <div key={friend.id} className="form-check">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    name="attendeeIds" 
                                    value={friend.id} 
                                    id={`friend-${friend.id}`}
                                    defaultChecked={currentAttendees.includes(friend.id)}
                                />
                                <label className="form-check-label" htmlFor={`friend-${friend.id}`}>
                                    {friend.displayName}
                                </label>
                            </div>
                        ))}
                    </div>
                  </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
              <button type="submit" className="btn btn-primary">Save</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function SignupForm({ onSignup, error, currentUser }) {
  return (
    <div className="d-flex align-items-center justify-content-center vh-100" style={{ backgroundColor: '#1a1a1b', color: '#ffffff' }}>
      <div className="w-100" style={{ maxWidth: '400px', padding: '24px' }}>
        <h1 className="h3 fw-bold mb-3 text-center">Complete Profile</h1>
        <p className="small text-center mb-4" style={{ color: '#818384' }}>
          Welcome! Let's set up your profile to get started with Wythe.
        </p>
        
        {currentUser && (
            <div className="alert border-0 py-2 text-center small mb-4" style={{ backgroundColor: '#272729', color: '#d7dadc' }}>
                Authenticated as: <strong style={{ color: '#ffffff' }}>{currentUser.email}</strong>
            </div>
        )}

        {error && <div className="alert alert-danger border-0 py-2 mb-4" style={{ borderRadius: '12px' }}>{error}</div>}
        
        <form onSubmit={onSignup}>
          <div className="mb-3">
            <label htmlFor="displayName" className="form-label small fw-bold" style={{ color: '#818384' }}>FULL NAME</label>
            <input 
              type="text" 
              className="form-control border-0 py-3 px-3 shadow-none" 
              id="displayName" 
              name="displayName" 
              placeholder="John Doe" 
              minLength="2" 
              maxLength="100" 
              required 
              style={{ backgroundColor: '#272729', color: '#ffffff', borderRadius: '12px' }}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="phone" className="form-label small fw-bold" style={{ color: '#818384' }}>PHONE NUMBER</label>
            <input 
              type="tel" 
              className="form-control border-0 py-3 px-3 shadow-none" 
              id="phone" 
              name="phone" 
              placeholder="+1 (555) 000-0000" 
              pattern="^[\d\s\-\+\(\)]{10,20}$" 
              required 
              style={{ backgroundColor: '#272729', color: '#ffffff', borderRadius: '12px' }}
            />
            <div className="form-text small mt-2" style={{ color: '#818384' }}>Required for notifications.</div>
          </div>
          
          <div className="d-grid gap-2">
            <button 
              type="submit" 
              className="btn btn-light py-2 fw-bold"
              style={{ borderRadius: '999px', fontSize: '14px' }}
            >
              Create Account
            </button>
            <button 
              type="button"
              className="btn btn-link py-2 small"
              style={{ color: '#4fbcff', textDecoration: 'none' }}
              onClick={() => window.location.href = '/auth/google/logout'}
            >
              Sign out
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default App;
